import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api/axios';
import toast from 'react-hot-toast';
import Pagination from '../components/Pagination';
import GlobalDateFilter from '../components/GlobalDateFilter';
import {
  MdSearch, MdFileDownload, MdRefresh, MdArrowUpward, MdArrowDownward,
  MdClose, MdArticle, MdCalendarToday, MdBarChart, MdOpenInNew,
} from 'react-icons/md';

/* ─────────────── helpers ─────────────── */
function downloadCSV(rows, subRows, filename) {
  const lines = [
    ['--- CATEGORY REPORT ---'],
    ['Category', 'Posts Count'],
    ...rows.map(r => [r.category, r.todayPosts]),
    [],
    ['--- SUBCATEGORY REPORT ---'],
    ['Subcategory', 'Posts Count'],
    ...subRows.map(s => [s.teluguName || s.subcategory, s.todayPosts])
  ];
  const csv  = lines.map(l => l.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

function getFilterTitleSuffix(filter, fromDate, toDate) {
  switch (filter) {
    case 'today':
      return 'Today';
    case 'yesterday':
      return 'Yesterday';
    case 'last7':
      return 'Last 7 Days';
    case 'last30':
      return 'Last 30 Days';
    case 'thisMonth':
      return 'This Month';
    case 'lastMonth':
      return 'Last Month';
    case 'custom':
      if (fromDate && toDate) {
        const formatStr = (dStr) => {
          const d = new Date(dStr + 'T00:00:00');
          return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
        };
        return `Custom Range (${formatStr(fromDate)} - ${formatStr(toDate)})`;
      }
      return 'Custom Range';
    default:
      return 'Today';
  }
}

function getFilterSubtitle(filter, reportDate, fromDate, toDate) {
  if (filter === 'today' || filter === 'yesterday') {
    const d = filter === 'yesterday'
      ? new Date(Date.now() - 86400000)
      : (reportDate ? new Date(reportDate + 'T00:00:00') : new Date());
    return d.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  }
  if (filter === 'custom' && fromDate && toDate) {
    return `${fromDate} to ${toDate}`;
  }
  return `Filtered by ${getFilterTitleSuffix(filter, fromDate, toDate)}`;
}

/* ─────────────── page ─────────────── */
export default function CategoryReport() {
  /* report state */
  const [report,        setReport]        = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [totalToday,    setTotalToday]    = useState(0);
  const [reportDate,    setReportDate]    = useState('');
  const [loading,       setLoading]       = useState(true);
  const [refreshing,    setRefreshing]    = useState(false);

  /* date filter state */
  const [dateFilter,  setDateFilter]  = useState('today');
  const [customRange, setCustomRange] = useState({ from: '', to: '' });

  /* search / sort / pagination on the category table */
  const [search,    setSearch]    = useState('');
  const [sortKey,   setSortKey]   = useState('todayPosts');    // 'category' | 'todayPosts'
  const [sortDir,   setSortDir]   = useState('desc');          // 'asc' | 'desc'
  const [page,      setPage]      = useState(1);
  const PAGE_SIZE = 15;

  /* drill-down modal */
  const [drillModal,   setDrillModal]   = useState(null);   // { title, type: 'category' | 'subcategory' }
  const [drillData,    setDrillData]    = useState([]);
  const [drillPage,    setDrillPage]    = useState(1);
  const [drillPaging,  setDrillPaging]  = useState({ page: 1, pages: 1, total: 0 });
  const [drillLoading, setDrillLoading] = useState(false);

  const refreshTimer = useRef(null);

  /* ── fetch report ── */
  const fetchReport = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const params = { filter: dateFilter };
      if (dateFilter === 'custom' && customRange.from && customRange.to) {
        params.from_date = customRange.from;
        params.to_date   = customRange.to;
        params.startDate = customRange.from;
        params.endDate   = customRange.to;
      }
      const res = await api.get('/admin/reports/category-today', { params });
      setReport(res.data.data || []);
      setSubcategories(res.data.subcategories || []);
      setTotalToday(res.data.totalPosts ?? res.data.totalToday ?? 0);
      setReportDate(res.data.date || '');
    } catch {
      toast.error('Failed to load category report');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [dateFilter, customRange.from, customRange.to]);

  useEffect(() => {
    fetchReport();
    // auto-refresh every 60 s
    refreshTimer.current = setInterval(() => fetchReport(true), 60_000);
    return () => clearInterval(refreshTimer.current);
  }, [fetchReport]);

  /* ── drill-down fetch ── */
  const fetchDrill = useCallback(async (item, type = 'category', p = 1) => {
    setDrillLoading(true);
    try {
      const params = { page: p, limit: 10, filter: dateFilter };
      if (type === 'subcategory') {
        params.subcategory = item;
      } else {
        params.category = item;
      }
      if (dateFilter === 'custom' && customRange.from && customRange.to) {
        params.from_date = customRange.from;
        params.to_date   = customRange.to;
        params.startDate = customRange.from;
        params.endDate   = customRange.to;
      }
      const url = type === 'subcategory'
        ? '/admin/reports/subcategory-today/articles'
        : '/admin/reports/category-today/articles';
      const res = await api.get(url, { params });
      setDrillData(res.data.data || []);
      setDrillPaging(res.data.pagination || { page: 1, pages: 1, total: 0 });
      setDrillPage(p);
    } catch {
      toast.error('Failed to load articles');
    } finally {
      setDrillLoading(false);
    }
  }, [dateFilter, customRange.from, customRange.to]);

  const openDrill = (item, type = 'category') => {
    setDrillModal({ title: item, type });
    setDrillData([]);
    setDrillPage(1);
    fetchDrill(item, type, 1);
  };

  /* ── sorting ── */
  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir(key === 'todayPosts' ? 'desc' : 'asc'); }
    setPage(1);
  };

  /* ── derived data for categories ── */
  const filtered = report.filter(r =>
    r.category.toLowerCase().includes(search.toLowerCase())
  );
  const sorted = [...filtered].sort((a, b) => {
    let va = a[sortKey], vb = b[sortKey];
    if (typeof va === 'string') va = va.toLowerCase();
    if (typeof vb === 'string') vb = vb.toLowerCase();
    if (va < vb) return sortDir === 'asc' ? -1 : 1;
    if (va > vb) return sortDir === 'asc' ?  1 : -1;
    return 0;
  });
  const totalPages  = Math.ceil(sorted.length / PAGE_SIZE) || 1;
  const paginated   = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const maxPosts    = Math.max(...report.map(r => r.todayPosts), 1);
  const maxSubPosts = Math.max(...subcategories.map(s => s.todayPosts), 1);

  const filterTitleSuffix = getFilterTitleSuffix(dateFilter, customRange.from, customRange.to);
  const filterSubtitle    = getFilterSubtitle(dateFilter, reportDate, customRange.from, customRange.to);

  /* ─────────────── render ─────────────── */
  if (loading) return <div className="page-loader">Loading report…</div>;

  return (
    <div className="page cr-page" style={{ opacity: refreshing ? 0.7 : 1, transition: 'opacity 0.2s' }}>

      {/* ── header ── */}
      <div className="page-header cr-header">
        <div>
          <h2 className="cr-title">Category Report — {filterTitleSuffix}</h2>
          <p className="cr-subtitle">
            <MdCalendarToday style={{ verticalAlign: 'middle', marginRight: 4 }} />
            {filterSubtitle}
          </p>
        </div>
        <div className="cr-header-actions">
          <GlobalDateFilter
            filter={dateFilter}
            fromDate={customRange.from}
            toDate={customRange.to}
            onChange={(f, from, to) => {
              setDateFilter(f);
              setCustomRange({ from, to });
              setPage(1);
            }}
          />
          <button
            className="cr-btn cr-btn-ghost"
            onClick={() => fetchReport(true)}
            disabled={refreshing}
            title="Refresh report"
          >
            <MdRefresh className={refreshing ? 'cr-spin' : ''} />
            Refresh
          </button>
          <button
            className="cr-btn cr-btn-primary"
            onClick={() => downloadCSV(sorted, subcategories, `category-subcategory-report-${dateFilter}-${reportDate || 'date'}.csv`)}
          >
            <MdFileDownload />
            Export CSV
          </button>
        </div>
      </div>

      {/* ── summary cards ── */}
      <div className="cr-summary-row">
        <div className="cr-card cr-card-highlight">
          <div className="cr-card-icon"><MdArticle /></div>
          <div>
            <div className="cr-card-value">{totalToday}</div>
            <div className="cr-card-label">Total Posts ({filterTitleSuffix})</div>
          </div>
        </div>
        <div className="cr-card">
          <div className="cr-card-icon cr-icon-blue"><MdBarChart /></div>
          <div>
            <div className="cr-card-value">{report.filter(r => r.todayPosts > 0).length}</div>
            <div className="cr-card-label">Active Categories</div>
          </div>
        </div>
        <div className="cr-card">
          <div className="cr-card-icon cr-icon-gray"><MdBarChart /></div>
          <div>
            <div className="cr-card-value">{report.filter(r => r.todayPosts === 0).length}</div>
            <div className="cr-card-label">Silent Categories</div>
          </div>
        </div>
        <div className="cr-card">
          <div className="cr-card-icon cr-icon-green"><MdBarChart /></div>
          <div>
            <div className="cr-card-value">
              {report.length > 0
                ? report.reduce((best, r) => r.todayPosts > best.todayPosts ? r : best, report[0]).category
                : '—'}
            </div>
            <div className="cr-card-label">Most Active</div>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* ── 1. CATEGORY REPORT TABLE ── */}
      {/* ═══════════════════════════════════════════════════════════ */}
      <div className="cr-toolbar">
        <div className="cr-search-wrap">
          <MdSearch className="cr-search-icon" />
          <input
            className="cr-search-input"
            placeholder="Search category…"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
          />
          {search && (
            <button className="cr-search-clear" onClick={() => { setSearch(''); setPage(1); }}>
              <MdClose />
            </button>
          )}
        </div>
        <div className="cr-results-info">
          {filtered.length} categor{filtered.length === 1 ? 'y' : 'ies'} found
        </div>
      </div>

      <div className="cr-table-wrap">
        <table className="cr-table">
          <thead>
            <tr>
              <th className="cr-th cr-th-num">#</th>
              <th
                className={`cr-th cr-th-sortable ${sortKey === 'category' ? 'cr-th-active' : ''}`}
                onClick={() => toggleSort('category')}
              >
                Category
                {sortKey === 'category'
                  ? (sortDir === 'asc' ? <MdArrowUpward className="cr-sort-icon" /> : <MdArrowDownward className="cr-sort-icon" />)
                  : <MdArrowUpward className="cr-sort-icon cr-sort-muted" />}
              </th>
              <th
                className={`cr-th cr-th-sortable cr-th-right ${sortKey === 'todayPosts' ? 'cr-th-active' : ''}`}
                onClick={() => toggleSort('todayPosts')}
              >
                {dateFilter === 'today' ? "Today's Posts" : "Posts Count"}
                {sortKey === 'todayPosts'
                  ? (sortDir === 'asc' ? <MdArrowUpward className="cr-sort-icon" /> : <MdArrowDownward className="cr-sort-icon" />)
                  : <MdArrowDownward className="cr-sort-icon cr-sort-muted" />}
              </th>
              <th className="cr-th">Progress</th>
              <th className="cr-th cr-th-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {paginated.length === 0 ? (
              <tr>
                <td colSpan={5} className="cr-empty">No category data available for the selected date range.</td>
              </tr>
            ) : (
              paginated.map((row, idx) => {
                const pct = maxPosts > 0 ? (row.todayPosts / maxPosts) * 100 : 0;
                const rank = (page - 1) * PAGE_SIZE + idx + 1;
                return (
                  <tr key={row.category} className={`cr-tr ${row.todayPosts > 0 ? 'cr-tr-active' : 'cr-tr-zero'}`}>
                    <td className="cr-td cr-td-num">{rank}</td>
                    <td className="cr-td">
                      <span className="cr-cat-name">{row.category}</span>
                    </td>
                    <td className="cr-td cr-td-right">
                      <span className={`cr-badge ${row.todayPosts > 0 ? 'cr-badge-active' : 'cr-badge-zero'}`}>
                        {row.todayPosts}
                      </span>
                    </td>
                    <td className="cr-td cr-td-bar">
                      <div className="cr-progress-bg">
                        <div
                          className="cr-progress-fill"
                          style={{ width: `${pct}%` }}
                          title={`${pct.toFixed(1)}% of max`}
                        />
                      </div>
                    </td>
                    <td className="cr-td cr-td-right">
                      <button
                        className="cr-drilldown-btn"
                        onClick={() => openDrill(row.category, 'category')}
                        disabled={row.todayPosts === 0}
                        title={row.todayPosts === 0 ? 'No articles' : `View ${row.todayPosts} articles`}
                      >
                        <MdOpenInNew />
                        View
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* ── 2. SUBCATEGORY REPORT (BELOW CATEGORY REPORT) ── */}
      {/* ═══════════════════════════════════════════════════════════ */}
      <div style={{ marginTop: '24px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
          <div>
            <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text)', margin: 0 }}>
              Subcategory Report — {filterTitleSuffix}
            </h3>
            <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', margin: '4px 0 0' }}>
              Regional distribution based on state & location for {filterTitleSuffix.toLowerCase()}
            </p>
          </div>
        </div>

        <div className="cr-table-wrap">
          <table className="cr-table">
            <thead>
              <tr>
                <th className="cr-th cr-th-num">#</th>
                <th className="cr-th">Subcategory</th>
                <th className="cr-th cr-th-right">
                  {dateFilter === 'today' ? "Today's Posts" : "Posts Count"}
                </th>
                <th className="cr-th">Progress</th>
                <th className="cr-th cr-th-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {subcategories.length === 0 ? (
                <tr>
                  <td colSpan={5} className="cr-empty">No subcategory data available for the selected date range.</td>
                </tr>
              ) : (
                subcategories.map((sub, idx) => {
                  const pct = maxSubPosts > 0 ? (sub.todayPosts / maxSubPosts) * 100 : 0;
                  const displayTitle = sub.teluguName || sub.subcategory;
                  return (
                    <tr key={sub.subcategory} className={`cr-tr ${sub.todayPosts > 0 ? 'cr-tr-active' : 'cr-tr-zero'}`}>
                      <td className="cr-td cr-td-num">{idx + 1}</td>
                      <td className="cr-td">
                        <span className="cr-cat-name" style={{ fontWeight: 600, fontSize: '0.95rem' }}>{displayTitle}</span>
                      </td>
                      <td className="cr-td cr-td-right">
                        <span className={`cr-badge ${sub.todayPosts > 0 ? 'cr-badge-active' : 'cr-badge-zero'}`}>
                          {sub.todayPosts}
                        </span>
                      </td>
                      <td className="cr-td cr-td-bar">
                        <div className="cr-progress-bg">
                          <div
                            className="cr-progress-fill"
                            style={{ width: `${pct}%`, background: '#2563EB' }}
                            title={`${pct.toFixed(1)}% of max`}
                          />
                        </div>
                      </td>
                      <td className="cr-td cr-td-right">
                        <button
                          className="cr-drilldown-btn"
                          onClick={() => openDrill(sub.subcategory, 'subcategory')}
                          disabled={sub.todayPosts === 0}
                          title={sub.todayPosts === 0 ? 'No articles' : `View ${sub.todayPosts} articles`}
                        >
                          <MdOpenInNew />
                          View
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── pagination at bottom after both reports ── */}
      {totalPages > 1 && (
        <div style={{ marginTop: '16px' }}>
          <Pagination
            pagination={{ page, pages: totalPages }}
            onPageChange={setPage}
          />
        </div>
      )}

      {/* ───────────── drill-down modal ───────────── */}
      {drillModal && (
        <div className="cr-modal-overlay" onClick={() => setDrillModal(null)}>
          <div className="cr-modal" onClick={e => e.stopPropagation()}>
            <div className="cr-modal-header">
              <div>
                <h3 className="cr-modal-title">
                  {drillModal.title} {drillModal.type === 'subcategory' ? '(Subcategory)' : ''}
                </h3>
                <p className="cr-modal-sub">Published articles ({filterTitleSuffix})</p>
              </div>
              <button className="cr-modal-close" onClick={() => setDrillModal(null)}>
                <MdClose />
              </button>
            </div>

            {drillLoading ? (
              <div className="cr-modal-loader">Loading articles…</div>
            ) : drillData.length === 0 ? (
              <div className="cr-modal-empty">No published articles found for this period.</div>
            ) : (
              <>
                <div className="cr-modal-count">
                  {drillPaging.total} article{drillPaging.total !== 1 ? 's' : ''} in {filterTitleSuffix}
                </div>
                <div className="cr-modal-list">
                  {drillData.map((article, i) => (
                    <div key={article.id} className="cr-article-row">
                      <div className="cr-article-num">{(drillPage - 1) * 10 + i + 1}</div>
                      {article.image_url && (
                        <img
                          className="cr-article-thumb"
                          src={article.image_url}
                          alt=""
                          onError={e => { e.target.style.display = 'none'; }}
                        />
                      )}
                      <div className="cr-article-info">
                        <div className="cr-article-title">{article.title}</div>
                        <div className="cr-article-meta">
                          <span>{article.category}</span>
                          <span>·</span>
                          <span>{article.city}</span>
                          <span>·</span>
                          <span>{article.author_name}</span>
                          <span>·</span>
                          <span>{new Date(article.published_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</span>
                          <span>·</span>
                          <span>{article.view_count} views</span>
                          <span>·</span>
                          <span>{article.like_count} likes</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {drillPaging.pages > 1 && (
                  <Pagination
                    pagination={{ page: drillPage, pages: drillPaging.pages }}
                    onPageChange={p => fetchDrill(drillModal.title, drillModal.type, p)}
                  />
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

